// Progress percentages for each screen in the ABOUT_YOUR_DOGS section
const aboutYourDogsProgress: Record<AboutYourDogsScreenNames, number> = {
	AYD_NOD_INTRO: 0,
	AYD_INTRO: 0,
	Q1: 0,
	Q2: 0,
	Q3: 15,
	Q4: 30,
	Q5: 45,
	Q6: 60,
	Q6_1: 75,
	Q6_2: 75,
	Q6_3: 80,
	Q7: 90,
	Q7_1: 95,
};

/**
 * Given the current screen and form data, returns the next screen for the about your dogs route
 * @param currentScreen	- The current screen
 * @param formData	- The form data
 * @param _questionSchema - The question schema
 * @returns The next screen
 */
export const getNextScreenAboutYourDogsRoute = (
	surveyState: NDS.SurveyState,
	formData: NDS.FormData,
	questionSchema: NDS.QuestionSchema,
): NDS.RouteChange => {
	const screenState = surveyState.routeStack[surveyState.routeStack.length - 1];
	const dogIndex = Object.values(screenState.dogsState).find(
		(dog) => dog.isActive,
	)?.index as 1 | 2 | 3 | 4 | 5;
	switch (screenState.screen as AboutYourDogsScreenNames) {
		// Welcome back to the National Dog Survey.
		case "AYD_NOD_INTRO": {
			surveyState.routeStack.push({
				...screenState,
				route: "ABOUT_YOUR_DOGS",
				screen: "Q1",
			});

			surveyState.surveyProgress = {
				...surveyState.surveyProgress,
				ABOUT_YOUR_DOGS: aboutYourDogsProgress.Q1,
			};

			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
			};
		}

		// How many dogs do you currently own?
		case "Q1": {
			const userAnswer = formData.nds_q_1_number_of_dogs;

			const options = questionSchema.ABOUT_YOUR_DOGS.Q1.fields[0].options;
			const noDogsAnswer = options[0].value;

			if (userAnswer === noDogsAnswer) {
				// Clearing the dog names if they have input them.
				formData.nds_q_2_dog_name_1 = undefined;
				formData.nds_q_2_dog_name_2 = undefined;
				formData.nds_q_2_dog_name_3 = undefined;
				formData.nds_q_2_dog_name_4 = undefined;
				formData.nds_q_2_dog_name_5 = undefined;
				formData.nds_q_2_dog_name_prefer_not_to_say = undefined;

				surveyState.surveyProgress = {
					THOUGHTS_ON_DOGS: 0,
					ABOUT_YOU: -1,
				};

				surveyState.routeStack.push({
					...screenState,
					route: "THOUGHTS_ON_DOGS",
					screen: "TOD_INTRO",
					dogCount: 0,
				});
				return {
					surveyState: { ...surveyState },
					formData: { ...formData },
				};
			}

			const numberOfDogs = options.findIndex(
				(option) => option.value === userAnswer,
			) as 1 | 2 | 3 | 4 | 5;

			surveyState.surveyProgress = {
				...surveyState.surveyProgress,
				ABOUT_YOUR_DOGS: aboutYourDogsProgress.AYD_INTRO,
			};

			surveyState.routeStack.push({
				...screenState,
				route: "ABOUT_YOUR_DOGS",
				screen: "AYD_INTRO",
				dogCount: numberOfDogs,
			});
			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
			};
		}

		// About your [dogNum]
		case "AYD_INTRO": {
			// Clearing the dog names if they have input them as they may have changed how many dogs they have
			formData.nds_q_2_dog_name_1 = undefined;
			formData.nds_q_2_dog_name_2 = undefined;
			formData.nds_q_2_dog_name_3 = undefined;
			formData.nds_q_2_dog_name_4 = undefined;
			formData.nds_q_2_dog_name_5 = undefined;
			formData.nds_q_2_dog_name_prefer_not_to_say = undefined;

			surveyState.surveyProgress = {
				...surveyState.surveyProgress,
				ABOUT_YOUR_DOGS: aboutYourDogsProgress.Q2,
			};

			surveyState.routeStack.push({
				...screenState,
				route: "ABOUT_YOUR_DOGS",
				screen: "Q2",
			});

			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
			};
		}

		// What's your dog's name?
		case "Q2": {
			const options = questionSchema.ABOUT_YOUR_DOGS.Q1.fields[0].options;
			const userAnswer = formData.nds_q_1_number_of_dogs;

			const numberOfDogs = options.findIndex(
				(option) => option.value === userAnswer,
			) as 1 | 2 | 3 | 4 | 5;

			const dogNames = [
				formData.nds_q_2_dog_name_1,
				formData.nds_q_2_dog_name_2,
				formData.nds_q_2_dog_name_3,
				formData.nds_q_2_dog_name_4,
				formData.nds_q_2_dog_name_5,
			].filter(Boolean);

			const dogsState: NDS.DogState = {};
			for (let i = 0; i < numberOfDogs; i++) {
				const dogName = dogNames[i] as string;
				const dogIndex = (i + 1) as 1 | 2 | 3 | 4 | 5;

				const safeDogName =
					!dogName || dogName === "Prefer not to say"
						? {
								1: "your most recent dog",
								2: "your second most recent dog",
								3: "your third most recent dog",
								4: "your fourth most recent dog",
								5: "your fifth most recent dog",
							}[dogIndex]
						: dogNames[i];

				dogsState[`${safeDogName}*!*${dogIndex}`] = {
					isActive: dogIndex === 1,
					index: dogIndex,
				};
			}

			surveyState.surveyProgress = {
				...surveyState.surveyProgress,
				ABOUT_YOUR_DOGS: aboutYourDogsProgress.Q3,
			};

			surveyState.routeStack.push({
				...screenState,
				route: "ABOUT_YOUR_DOGS",
				screen: "Q3",
				dogCount: numberOfDogs,
				dogsState,
			});
			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
			};
		}

		// How old is [dogName]?
		case "Q3": {
			surveyState.surveyProgress = {
				...surveyState.surveyProgress,
				ABOUT_YOUR_DOGS: aboutYourDogsProgress.Q4,
			};

			surveyState.routeStack.push({
				...screenState,
				route: "ABOUT_YOUR_DOGS",
				screen: "Q4",
			});
			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
			};
		}

		// Is [dogName] female or male, and have they been neutered?
		case "Q4": {
			surveyState.surveyProgress = {
				...surveyState.surveyProgress,
				ABOUT_YOUR_DOGS: aboutYourDogsProgress.Q5,
			};

			surveyState.routeStack.push({
				...screenState,
				route: "ABOUT_YOUR_DOGS",
				screen: "Q5",
			});
			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
			};
		}

		// Is [dogName] the first dog you have owned as an adult?
		case "Q5": {
			surveyState.surveyProgress = {
				...surveyState.surveyProgress,
				ABOUT_YOUR_DOGS: aboutYourDogsProgress.Q6,
			};

			surveyState.routeStack.push({
				...screenState,
				route: "ABOUT_YOUR_DOGS",
				screen: "Q6",
			});
			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
			};
		}

		// Which of the following best describes [dogName]?
		case "Q6": {
			const breedOptions = questionSchema.ABOUT_YOUR_DOGS.Q6.fields[0].options;

			const pureBreedOption = breedOptions[0].value;
			const crossBreedOption = breedOptions[1].value;

			const selectedOption = formData[`nds_q_6_breed_type_${dogIndex}`];

			if (selectedOption === pureBreedOption) {
				// Clear the mixed breed answer if it exists
				formData[`nds_q_6_2_first_crossbreed_${dogIndex}`] = undefined;

				formData[`nds_q_6_2_first_crossbreed_unlisted_${dogIndex}`] = undefined;

				formData[`nds_q_6_2_second_crossbreed_${dogIndex}`] = undefined;

				formData[`nds_q_6_2_second_crossbreed_unlisted_${dogIndex}`] =
					undefined;

				surveyState.surveyProgress = {
					...surveyState.surveyProgress,
					ABOUT_YOUR_DOGS: aboutYourDogsProgress.Q6_1,
				};

				surveyState.routeStack.push({
					...screenState,
					route: "ABOUT_YOUR_DOGS",
					screen: "Q6_1",
				});
				return {
					surveyState: { ...surveyState },
					formData: { ...formData },
				};
			}
			if (selectedOption === crossBreedOption) {
				// Clear the single breed answer if it exists
				formData[`nds_q_6_1_single_breed_${dogIndex}`] = undefined;
				formData[`nds_q_6_1_single_breed_unlisted_${dogIndex}`] = undefined;

				surveyState.surveyProgress = {
					...surveyState.surveyProgress,
					ABOUT_YOUR_DOGS: aboutYourDogsProgress.Q6_2,
				};

				surveyState.routeStack.push({
					...screenState,
					route: "ABOUT_YOUR_DOGS",
					screen: "Q6_2",
				});
				return {
					surveyState: { ...surveyState },
					formData: { ...formData },
				};
			}

			// Else it must be unknown mix

			// Clear the mixed breed answer if it exists
			formData[`nds_q_6_2_first_crossbreed_${dogIndex}`] = undefined;

			formData[`nds_q_6_2_first_crossbreed_unlisted_${dogIndex}`] = undefined;

			formData[`nds_q_6_2_second_crossbreed_${dogIndex}`] = undefined;

			formData[`nds_q_6_2_second_crossbreed_unlisted_${dogIndex}`] = undefined;

			// Clear the single breed answer if it exists
			formData[`nds_q_6_1_single_breed_${dogIndex}`] = undefined;
			formData[`nds_q_6_1_single_breed_unlisted_${dogIndex}`] = undefined;

			surveyState.surveyProgress = {
				...surveyState.surveyProgress,
				ABOUT_YOUR_DOGS: aboutYourDogsProgress.Q6_3,
			};

			surveyState.routeStack.push({
				...screenState,
				route: "ABOUT_YOUR_DOGS",
				screen: "Q6_3",
			});
			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
			};
		}

		// What is [dogName]'s breed?
		case "Q6_1": {
			const userAnswer = formData[`nds_q_6_1_single_breed_${dogIndex}`];

			surveyState.surveyProgress = {
				...surveyState.surveyProgress,
				ABOUT_YOUR_DOGS: aboutYourDogsProgress.Q7,
			};

			if (
				!userAnswer ||
				["Breed not listed", "Unknown breed", "Prefer not to say"].includes(
					userAnswer as string,
				)
			) {
				// We want to get an idea of the size of the dog
				surveyState.routeStack.push({
					...screenState,
					route: "ABOUT_YOUR_DOGS",
					screen: "Q6_3",
				});
				return {
					surveyState: { ...surveyState },
					formData: { ...formData },
				};
			}

			// We know the size of the dog from the breed
			surveyState.routeStack.push({
				...screenState,
				route: "ABOUT_YOUR_DOGS",
				screen: "Q7",
			});
			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
			};
		}

		// What is [dogName]'s breed mix?
		case "Q6_2": {
			// We always want to know the size of the dog for any crossbreed
			surveyState.surveyProgress = {
				...surveyState.surveyProgress,
				ABOUT_YOUR_DOGS: aboutYourDogsProgress.Q6_3,
			};

			surveyState.routeStack.push({
				...screenState,
				route: "ABOUT_YOUR_DOGS",
				screen: "Q6_3",
			});
			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
			};
		}

		// What size is [dogName]? Or if they are not fully developed how big do you think they will be?
		case "Q6_3": {
			surveyState.surveyProgress = {
				...surveyState.surveyProgress,
				ABOUT_YOUR_DOGS: aboutYourDogsProgress.Q7,
			};

			surveyState.routeStack.push({
				...screenState,
				route: "ABOUT_YOUR_DOGS",
				screen: "Q7",
			});
			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
			};
		}

		// Which of these has [dogName] had?
		case "Q7": {
			const options = questionSchema.ABOUT_YOUR_DOGS.Q7.fields[0].options;
			const noneOfTheAboveOption = options[options.length - 1].value;

			if (
				!formData.nds_q_7_services ||
				formData.nds_q_7_services.length === 0 ||
				formData.nds_q_7_services.includes(noneOfTheAboveOption)
			) {
				surveyState.surveyProgress = {
					...surveyState.surveyProgress,
					ABOUT_YOUR_DOGS: 100,
					CHOOSING_YOUR_DOG: 0,
				};

				surveyState.routeStack.push({
					...screenState,
					route: "CHOOSING_YOUR_DOG",
					screen: "CYD_INTRO",
				});
				return {
					surveyState: { ...surveyState },
					formData: { ...formData },
					transition: "ABOUT_YOUR_DOGS",
				};
			}

			surveyState.surveyProgress = {
				...surveyState.surveyProgress,
				ABOUT_YOUR_DOGS: aboutYourDogsProgress.Q7_1,
			};

			surveyState.routeStack.push({
				...screenState,
				route: "ABOUT_YOUR_DOGS",
				screen: "Q7_1",
			});
			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
			};
		}

		// Has [dogName] had any of these in the last 12 months?
		case "Q7_1": {
			surveyState.surveyProgress = {
				...surveyState.surveyProgress,
				ABOUT_YOUR_DOGS: 100, // Setting to 100% when moving to next section
				CHOOSING_YOUR_DOG: 0,
			};

			surveyState.routeStack.push({
				...screenState,
				route: "CHOOSING_YOUR_DOG",
				screen: "CYD_INTRO",
			});
			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
				transition: "ABOUT_YOUR_DOGS",
			};
		}
		default: {
			throw new Error(
				`getNextScreenABOUT_YOUR_DOGSsRoute: Unknown screen ${screenState.screen} in route ${screenState.route}`,
			);
		}
	}
};
